import axios, { ResponseType } from "axios";
import { ISystem } from "../../models/system";
import { isNullOrWhiteSpace } from "../../utils/string-helper";
import config from "../../config/config";

const setHeaders = (system?: string, contentType?: string) => {
	return {
		System: system ? system : undefined,
		"Content-Type": contentType,
		"x-csrf": "1",
	};
};

const getBaseUrl = (versionOverride?: string) => {
	let version = "v1";
	if (versionOverride) {
		version = versionOverride;
	}
	if (config.apiBaseUrl === "https://localhost:9090") {
		return `${config.apiBaseUrl}/${version}/`;
	}
	return `${config.apiBaseUrl}/${version}/`;
};

export const getAsync = async <T>(
	endpoint: string,
	system?: string,
	params?: Record<string, unknown>,
): Promise<T> => {
	const url = endpoint.startsWith("http") ? endpoint : `${getBaseUrl()}${endpoint}`;

	try {
		const res = await axios.get<T>(url, {
			headers: setHeaders(system),
			params: params,
			paramsSerializer: {
				indexes: null,
			},
			withCredentials: true,
		});
		return res.data;
	} catch (error: unknown) {
		return handleRequestError(error);
	}
};

export const getMultiple = async <T, R>(
	systems: ISystem[],
	endpoint: string,
	mapper: (x: T, systemName: string) => R,
	params?: Record<string, unknown>,
): Promise<Map<string, R>> => {
	const systemMap = new Map<string, R>();
	const promises: Promise<unknown>[] = [];
	systems.forEach((system) => {
		promises.push(
			getAsync<T>(endpoint, system.name, params).then((x) =>
				systemMap.set(system.name, mapper(x, system.name)),
			),
		);
	});
	await Promise.all<unknown>(promises);
	return systemMap;
};

export const postMultiple = async <T, K>(
	data: K,
	systems: string[],
	endpoint: string,
	params?: Record<string, unknown>,
): Promise<Map<string, T>> => {
	const systemMap = new Map<string, T>();
	const promises: Promise<unknown>[] = [];
	systems.forEach((system) => {
		promises.push(postAsync<T, K>(endpoint, data, system, params));
	});
	await Promise.all<unknown>(promises);
	return systemMap;
};

export const putAsync = async <T, K>(
	endpoint: string,
	data?: K,
	system?: string,
	params?: Record<string, unknown>,
): Promise<T> => {
	const url = `${getBaseUrl()}${endpoint}`;

	try {
		const res = await axios.put<T>(url, data, {
			headers: setHeaders(system),
			params: params,
			withCredentials: true,
		});
		return res.data;
	} catch (error: unknown) {
		return handleRequestError(error);
	}
};

export const postAsync = async <T, K>(
	endpoint: string,
	data: K,
	system?: string,
	params?: Record<string, unknown>,
	responseType?: ResponseType,
	contentType?: string,
): Promise<T> => {
	const url = `${getBaseUrl()}${endpoint}`;

	try {
		const res = await axios.post<T>(url, data, {
			headers: setHeaders(system, contentType),
			params: params,
			responseType: responseType,
			withCredentials: true,
		});
		return res.data;
	} catch (error: unknown) {
		return handleRequestError(error);
	}
};

export const deleteAsync = async <T>(
	endpoint: string,
	id?: string | number,
	system?: string,
	params?: Record<string, unknown>,
): Promise<T> => {
	const url = `${getBaseUrl()}${endpoint}${!isNullOrWhiteSpace(id?.toString()) ? "/" + id : ""}`;

	try {
		const res = await axios.delete<T>(url, {
			headers: setHeaders(system),
			params: params,
			withCredentials: true,
		});
		return res.data;
	} catch (error: unknown) {
		return handleRequestError(error);
	}
};

function handleRequestError(error: unknown): never {
	if (axios.isAxiosError(error) && error.response?.status === 401) {
		const returnUrl = encodeURIComponent(window.location.href);
		window.location.href = `${config.bffUrl}/login?returnUrl=${returnUrl}`;
	}

	if (error instanceof Error) {
		throw error;
	}

	throw new Error(String(error));
}
